import { useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Download,
  Settings2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  exportToCSV,
  exportToExcel,
  prepareTableData,
  generateFilename,
} from "@/lib/tableUtils";

export function DataTable({ tableContent, onCellClick }) {
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Prepare table data
  const { columns: tableColumns, data: tableData } = useMemo(() => {
    return prepareTableData(tableContent);
  }, [tableContent]);

  // Check if table has 20+ rows to show advanced features
  const showAdvancedFeatures = tableData.length >= 20;

  // Initialize TanStack Table
  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    state: {
      sorting,
      columnVisibility,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
  });

  // Export handlers
  const handleExportCSV = () => {
    const success = exportToCSV(
      tableContent.headers,
      tableContent.body,
      generateFilename('cleardemand-table', 'csv')
    );
    if (success) {
      toast.success("Table exported to CSV");
    } else {
      toast.error("Failed to export table");
    }
  };

  const handleExportExcel = () => {
    const success = exportToExcel(
      tableContent.headers,
      tableContent.body,
      generateFilename('cleardemand-table', 'xlsx')
    );
    if (success) {
      toast.success("Table exported to Excel");
    } else {
      toast.error("Failed to export table");
    }
  };

  // Get visible columns count
  const visibleColumnsCount = table.getVisibleLeafColumns().length;
  const totalColumnsCount = table.getAllLeafColumns().length;

  return (
    <div className="w-full space-y-3 border rounded-xl px-4 py-3 shadow-sm bg-white border-slate-200">
      {/* Toolbar - Only show for tables with 20+ rows */}
      {showAdvancedFeatures && (
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search in table..."
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-9 h-9 bg-slate-50 border-slate-200 focus:bg-white"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Column Visibility */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 gap-2 border-slate-200 hover:bg-slate-50"
                >
                  <Settings2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Columns</span>
                  <span className="text-xs text-slate-500">
                    ({visibleColumnsCount}/{totalColumnsCount})
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table.getAllLeafColumns().map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {column.columnDef.header}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Export CSV */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              className="h-9 gap-2 border-slate-200 hover:bg-slate-50"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">CSV</span>
            </Button>

            {/* Export Excel */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              className="h-9 gap-2 border-slate-200 hover:bg-slate-50"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Excel</span>
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden border-slate-200">
        {/* Use vertical scrolling with max height for large tables, no horizontal scroll */}
        <div className={`overflow-y-auto ${tableData.length > 10 ? 'max-h-[500px]' : ''}`}>
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-slate-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="bg-slate-50 hover:bg-slate-50">
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className="font-semibold text-slate-900"
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort()
                                ? "flex items-center gap-2 cursor-pointer select-none hover:text-blue-600 transition-colors"
                                : ""
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <div className="flex flex-col">
                                {header.column.getIsSorted() === "asc" ? (
                                  <ArrowUp className="w-4 h-4 text-blue-600" />
                                ) : header.column.getIsSorted() === "desc" ? (
                                  <ArrowDown className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ArrowUpDown className="w-4 h-4 text-slate-400" />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      const cellValue = cell.getValue();
                      const headerText = tableContent.headers[cellIndex];

                      // Check if this is an ID column (first column or contains "ID" in header)
                      const isIDColumn = cellIndex === 0 ||
                                       (headerText && headerText.toLowerCase().includes('id'));

                      // Make ID cells clickable
                      const isClickable = isIDColumn && onCellClick && cellValue;

                      // Debug logging
                      if (cellIndex === 0) {
                        console.log('[DataTable] First cell in row:', {
                          cellValue,
                          headerText,
                          isIDColumn,
                          onCellClickExists: !!onCellClick,
                          isClickable,
                          cellValueType: typeof cellValue
                        });
                      }

                      return (
                        <TableCell
                          key={cell.id}
                          className={`break-words max-w-xs ${
                            isClickable
                              ? 'cursor-pointer text-blue-600 hover:text-blue-800 hover:underline font-medium'
                              : ''
                          }`}
                          onClick={() => {
                            console.log('[DataTable] Cell clicked:', {
                              cellValue,
                              headerText,
                              isClickable,
                              cellIndex
                            });
                            if (isClickable) {
                              console.log('[DataTable] Calling onCellClick with:', cellValue, headerText);
                              onCellClick(cellValue, headerText);
                            } else {
                              console.log('[DataTable] Cell not clickable, skipping');
                            }
                          }}
                          title={isClickable ? `Click to view details for ${headerText}: ${cellValue}` : ''}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={tableColumns.length}
                    className="h-24 text-center text-slate-500"
                  >
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {table.getPageCount() > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          {/* Results info */}
          <div className="text-sm text-slate-600">
            Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{" "}
            of {table.getFilteredRowModel().rows.length} results
          </div>

          {/* Pagination controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className="h-8 w-8 p-0"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              className="h-8 w-8 p-0"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Rows per page */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Rows per page:</span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
              className="h-8 px-2 border rounded-md text-sm border-slate-200 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[5, 10, 20, 30, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
